import { useState, useEffect } from "react";
import {
  Mail,
  AlertTriangle,
  Search,
  Filter,
  Users,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Send,
  X,
  History,
  ChevronDown,
  ChevronUp,
  Clock,
  XCircle,
  Loader2,
} from "lucide-react";
import {
  useGetMembersFinancialStatusQuery,
  useGetChaptersQuery,
  useGetStatesQuery,
  useGetUserCategoriesQuery,
  useSendReminderToDefaultersMutation,
  useGetReminderLogsQuery,
} from "../api/billingApi";
import { formatCurrency } from "../../../utils/formatters";
import useDebounce from "../components/useDebounce";

function StatusBadge({ status }: { status: string }) {
  if (status === "sent")
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="w-3 h-3" /> Sent
      </span>
    );
  if (status === "failed")
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
        <XCircle className="w-3 h-3" /> Failed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
      <Loader2 className="w-3 h-3 animate-spin" /> Queued
    </span>
  );
}

function BatchRow({ batch }: { batch: any }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching } = useGetReminderLogsQuery(
    { batch_id: batch.batch_id },
    { skip: !open }
  );
  const entries = data?.entries ?? [];

  const sentDate = batch.sent_at
    ? new Date(batch.sent_at).toLocaleString("en-NG", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      {/* Batch header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/70 transition-colors text-left"
      >
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <p className="text-[12px] font-black text-gray-900">{sentDate}</p>
            <p className="text-[10px] text-gray-400 font-medium mt-0.5">
              Year: {batch.year ?? "All"} &nbsp;·&nbsp; {batch.total} recipient
              {batch.total !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {Number(batch.sent) > 0 && (
              <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                {batch.sent} sent
              </span>
            )}
            {Number(batch.failed) > 0 && (
              <span className="text-[10px] font-black text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                {batch.failed} failed
              </span>
            )}
            {Number(batch.queued) > 0 && (
              <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                {batch.queued} queued
              </span>
            )}
          </div>
          {open ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Entries */}
      {open && (
        <div className="border-t border-gray-100">
          {isFetching ? (
            <div className="flex items-center justify-center py-8 gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-[11px] font-medium">Loading entries…</span>
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-gray-50/60 border-b border-gray-100">
                  <th className="text-left px-5 py-2.5 font-black text-gray-400 text-[10px] uppercase tracking-wider">
                    Member
                  </th>
                  <th className="text-left px-5 py-2.5 font-black text-gray-400 text-[10px] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-right px-5 py-2.5 font-black text-gray-400 text-[10px] uppercase tracking-wider">
                    Balance
                  </th>
                  <th className="text-center px-5 py-2.5 font-black text-gray-400 text-[10px] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-5 py-2.5 font-black text-gray-400 text-[10px] uppercase tracking-wider">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map((e: any) => (
                  <tr
                    key={e.id}
                    className={e.status === "failed" ? "bg-red-50/40" : ""}
                  >
                    <td className="px-5 py-3 font-black text-gray-800 uppercase">
                      {e.full_name?.trim() || e.name}
                    </td>
                    <td className="px-5 py-3 text-gray-500 font-medium">
                      {e.email}
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-red-600">
                      {formatCurrency(Number(e.balance))}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatusBadge status={e.status} />
                    </td>
                    <td className="px-5 py-3 text-gray-400 font-medium max-w-xs truncate">
                      {e.error_message ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function DemandNotePage() {
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState<number | "">(currentYear);
  const [chapterId, setChapterId] = useState("");
  const [stateId, setStateId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sent, setSent] = useState(false);

  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    setPage(1);
    setSent(false);
  }, [debouncedSearch, year, chapterId, stateId, categoryId]);

  const filters = {
    status: "not_up_to_date",
    year: year || undefined,
    chapter_id: chapterId || undefined,
    state_id: stateId || undefined,
    category_id: categoryId || undefined,
    search: debouncedSearch || undefined,
    page,
  };

  const { data, isFetching } = useGetMembersFinancialStatusQuery(filters);
  const members = data?.data ?? [];
  const pagination = data?.meta;

  const { data: chapters = [] } = useGetChaptersQuery();
  const { data: states = [] } = useGetStatesQuery();
  const { data: categoriesRes } = useGetUserCategoriesQuery();
  const categories = categoriesRes?.data ?? [];

  const { data: logsData, isFetching: loadingLogs } = useGetReminderLogsQuery({});
  const batches = logsData?.batches ?? [];

  const [sendReminder, { isLoading: sending }] =
    useSendReminderToDefaultersMutation();
  const [sendingUserId, setSendingUserId] = useState<string | null>(null);

  const totalOwing = data?.total_balance ?? 0;

  const handleSend = async () => {
    try {
      await sendReminder(filters).unwrap();
      setShowConfirm(false);
      setSent(true);
    } catch {
      setShowConfirm(false);
      alert("Failed to queue demand notes. Please try again.");
    }
  };

  const handleSendToOne = async (userId: string) => {
    setSendingUserId(userId);
    try {
      await sendReminder({ user_id: userId }).unwrap();
    } catch {
      alert("Failed to send demand note to this member.");
    } finally {
      setSendingUserId(null);
    }
  };

  const clearFilters = () => {
    setYear(currentYear);
    setChapterId("");
    setStateId("");
    setCategoryId("");
    setSearch("");
    setSent(false);
  };

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const formatLastReminded = (dateStr: string | null) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const diffDays = Math.floor(
      (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-red-600" />
            </div>
            <h1 className="text-xl font-black text-gray-900 tracking-tight">
              Demand Note
            </h1>
          </div>
          <p className="text-[11px] text-gray-500 font-medium tracking-wide pl-1">
            Send payment reminder emails to members with outstanding balances
          </p>
        </div>

        <button
          onClick={() => setShowConfirm(true)}
          disabled={members.length === 0 || sending || isFetching}
          className="flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-xl font-black text-[11px] uppercase tracking-widest transition-all shadow-sm active:scale-95"
        >
          <Send className="w-4 h-4" />
          Send Demand Note
          {pagination?.total != null && members.length > 0 && (
            <span className="bg-red-800/40 text-red-100 px-2 py-0.5 rounded-lg text-[10px] font-black">
              {pagination.total}
            </span>
          )}
        </button>
      </div>

      {/* Success banner */}
      {sent && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <p className="text-sm font-black text-emerald-800">
              Demand notes queued successfully
            </p>
            <p className="text-[11px] text-emerald-600 mt-0.5">
              Emails are being dispatched to all matching defaulters. Check the Send History below for delivery status.
            </p>
          </div>
          <button onClick={() => setSent(false)} className="ml-auto text-emerald-500 hover:text-emerald-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Defaulters</div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-red-500" />
            <span className="text-2xl font-black text-gray-900">
              {isFetching ? "—" : pagination?.total ?? members.length}
            </span>
          </div>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
          <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">Total Outstanding (All Filters)</div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-2xl font-black text-amber-700">
              {isFetching ? "—" : formatCurrency(totalOwing)}
            </span>
          </div>
        </div>
        <div className="bg-white border border-red-50 rounded-2xl p-5 shadow-sm col-span-2 md:col-span-1 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4 text-red-500" />
          </div>
          <p className="text-[11px] font-bold text-gray-700">
            Email sent to each defaulter with their balance and a payment link
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Filter Recipients</span>
          <button onClick={clearFilters} className="ml-auto text-[10px] font-bold text-gray-400 hover:text-gray-700 underline">
            Reset
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search member..."
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-[12px] font-medium focus:outline-none focus:border-gray-400"
            />
          </div>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value ? Number(e.target.value) : "")}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[12px] font-medium focus:outline-none focus:border-gray-400"
          >
            <option value="">All Years</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[12px] font-medium focus:outline-none focus:border-gray-400"
          >
            <option value="">All Chapters</option>
            {chapters.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={stateId}
            onChange={(e) => setStateId(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[12px] font-medium focus:outline-none focus:border-gray-400"
          >
            <option value="">All States</option>
            {states.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-[12px] font-medium focus:outline-none focus:border-gray-400"
          >
            <option value="">All Categories</option>
            {categories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Defaulters table */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">
            Members With Outstanding Balance
          </span>
          {isFetching && (
            <span className="text-[10px] text-gray-400 font-medium animate-pulse">Loading...</span>
          )}
        </div>

        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">#</th>
              <th className="text-left px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">Member</th>
              <th className="text-right px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">Total Invoiced</th>
              <th className="text-right px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">Total Paid</th>
              <th className="text-right px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">Balance Owing</th>
              <th className="text-center px-6 py-3 font-black text-gray-400 text-[10px] uppercase tracking-wider">Last Notified</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isFetching ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-3 bg-gray-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : members.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center text-gray-400 text-[12px] font-medium">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                  No defaulters found for the selected filters.
                </td>
              </tr>
            ) : (
              members.map((m: any, i: number) => {
                const lastReminded = formatLastReminded(m.last_reminded_at);
                const isThisSending = sendingUserId === m.id;
                return (
                  <tr key={m.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 text-gray-400 font-bold">
                      {(page - 1) * (pagination?.per_page ?? 20) + i + 1}
                    </td>
                    <td className="px-6 py-4 font-black text-gray-900 uppercase">{m.name}</td>
                    <td className="px-6 py-4 text-right font-mono font-bold text-gray-600">
                      {formatCurrency(Number(m.total_invoiced))}
                    </td>
                    <td className="px-6 py-4 text-right font-mono font-bold text-emerald-600">
                      {formatCurrency(Number(m.total_paid))}
                    </td>
                    <td className="px-6 py-4 text-right font-mono font-black text-red-600">
                      {formatCurrency(Number(m.balance))}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {lastReminded ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" />
                          {lastReminded}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-300 font-medium">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleSendToOne(m.id)}
                        disabled={isThisSending || !!sendingUserId}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                      >
                        {isThisSending ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        {isThisSending ? "Sending…" : "Send"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <span className="text-[11px] text-gray-400 font-medium">
              Page {pagination.current_page} of {pagination.last_page} — {pagination.total} members
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.current_page === 1}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                disabled={pagination.current_page === pagination.last_page}
                className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Send History */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <History className="w-4 h-4 text-gray-400" />
          <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">Send History</span>
          {loadingLogs && (
            <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin ml-auto" />
          )}
        </div>

        <div className="p-4 space-y-3">
          {!loadingLogs && batches.length === 0 && (
            <div className="text-center py-10 text-gray-400 text-[12px] font-medium">
              No demand notes have been sent yet.
            </div>
          )}
          {batches.map((batch: any) => (
            <BatchRow key={batch.batch_id} batch={batch} />
          ))}
        </div>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <Mail className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="font-black text-gray-900 text-lg">Send Demand Notes</h2>
                <p className="text-[11px] text-gray-500 font-medium">This will queue emails to all matching defaulters</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-[12px] text-amber-800 font-medium leading-relaxed">
                <strong className="font-black">
                  {pagination?.total ?? members.length} member{(pagination?.total ?? members.length) !== 1 ? "s" : ""}
                </strong>{" "}
                matching your current filters will receive a demand note.
                <br />
                <span className="text-amber-600 mt-1 block">
                  Delivery status will be tracked in Send History below.
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-[12px] font-black text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[12px] font-black uppercase tracking-wider transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="w-4 h-4" /> Confirm Send</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
